import { Image, useResizeObserver } from '@byecode/ui'
import { OSS_USER_DATA_PREFIX } from '@lighthouse/assets'
import type {
    ActorEvent,
    BaseFieldConfig,
    Field,
    FieldADTValue,
    FieldValue,
    GalleryViewOptions,
    GroupFieldValue,
    HighLightConditions,
    ObjectFitConfig,
    RatioConfig,
    RecordLikeProtocol,
    RichTextContentProtocol,
    ViewField
} from '@lighthouse/core'
import { EVENT_TYPE, GALLERY_AVATAR_SHAPE, GALLERY_AVATAR_SIZE, GALLERY_CARD_STYLE } from '@lighthouse/core'
import {
    ActionButton,
    fieldConvertValue,
    getImageFullUrlInApplication,
    getWithItemId,
    getWithRecordId,
    RowHighlighter,
    selectRecordFromPool,
    useActionRunningLoadings,
    useApplicationContext,
    useAtomData,
    verifyFieldValueEmpty,
    ViewFieldValuePreviewer
} from '@lighthouse/shared'
import { BreakPointSize, mergeRefs, useBreakpoint } from '@lighthouse/tools'
import { Avatar, Box, Card, Group, Stack } from '@mantine/core'
import { produce } from 'immer'
import type { CSSProperties, ReactNode } from 'react'
import React, { forwardRef, Fragment, memo, useCallback, useDeferredValue, useMemo } from 'react'

import { useViewBlockContext } from '../../context'
import { getImageFromField } from '../../utils'
import * as SC from './styles'
import {
    AVATAR_RADIUS,
    AVATAR_SIZES,
    isAvatarVisible,
    isBasicFieldVisible,
    isContentVisible,
    isFooterVisible,
    isHeadVisible
} from './utils'

interface GalleryRecordProps {
    id: string
    headCover?: Omit<BaseFieldConfig, 'align'> & ObjectFitConfig & RatioConfig
    columns: ViewField[]
    rowStyleConditions: Record<string, HighLightConditions>
    headAvatar: GalleryViewOptions['headAvatar']
    avatarAlignSelf?: CSSProperties['alignSelf']
    headTitle?: BaseFieldConfig
    headTags: GalleryViewOptions['headTags']
    contentTags: GalleryViewOptions['contentTags']
    footTags: GalleryViewOptions['footTags']
    recordStyle: GalleryViewOptions['style']
    style?: React.CSSProperties
    renderItem?: (record: RecordLikeProtocol) => ReactNode
    onRecordOperatorActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRenderButtonTitle: (v: RichTextContentProtocol, record?: RecordLikeProtocol) => string
}

function scaleOssImg(src: string | undefined) {
    if (!src) {
        return
    }

    if (src.startsWith(OSS_USER_DATA_PREFIX)) {
        return `${src}&x-oss-process=image/resize,l_1200`
    }

    return src
}

export const GalleryRecord = memo(
    forwardRef<HTMLDivElement, GalleryRecordProps>(
        (
            {
                id,
                headCover,
                columns,
                rowStyleConditions,
                headAvatar,
                headTitle,
                headTags,
                contentTags,
                footTags,
                recordStyle,
                style,
                renderItem,
                onRecordOperatorActionTrigger,
                onRecordClick,
                onRenderButtonTitle
            },
            ref
        ) => {
            const { loadings, handleActionTriggerWithLoading } = useActionRunningLoadings()
            const [footerActionsContainerRef, rect] = useResizeObserver<HTMLDivElement>()
            const cardBreakPoint = useMemo(() => {
                return rect.width < 220 ? 'sm' : 'md'
            }, [rect.width])
            const { uniqueId, blockData } = useViewBlockContext()
            const { config } = blockData
            const { eventList, recordOperator } = config
            const { appId } = useApplicationContext()
            const { recordPoolAtom, dataSource } = useViewBlockContext()
            const { schema, id: dsId } = dataSource
            const { ref: innerRef, breakPoint, width } = useBreakpoint()
            const recordClickEvent = useMemo(() => eventList?.find(ev => ev.type === EVENT_TYPE.RECORD_CLICK), [eventList])
            const clickable = !!recordClickEvent || !!onRecordClick

            // 使用 Map 的 O(1) 查找，useMemo 保持 selector 引用稳定
            const selector = useMemo(() => selectRecordFromPool(dsId, id), [dsId, id])
            const record = useDeferredValue(useAtomData(recordPoolAtom, selector))

            const avatarAlignSelf = useMemo(() => {
                if (headAvatar?.position !== 'top') {
                    return 'flex-start'
                }
                if (!headAvatar?.align || headAvatar?.align === 'left') {
                    return 'flex-start'
                }
                if (headAvatar?.align === 'right') {
                    return 'flex-end'
                }
                return 'center'
            }, [headAvatar?.position, headAvatar?.align])

            const coverRatio = useMemo(() => {
                if (!headCover?.ratio) {
                    return 4 / 3
                }
                const [x, y] = headCover.ratio.split(':')
                return Number(x) / Number(y)
            }, [headCover?.ratio])

            const { content: recordContent } = record || ({ id: '', content: {} } as RecordLikeProtocol)

            const [recordOperatorFooterActions, recordOperatorDropdownActions] = useMemo(() => {
                if (!recordOperator) {
                    return [undefined, undefined]
                }
                return [recordOperator.slice(0, 2), recordOperator.slice(2)]
            }, [recordOperator])

            const getFieldValue = useCallback((filed?: Field, value?: FieldValue['value']) => {
                if (!filed || !value) {
                    return ''
                }
                const fieldADTValue = { ...filed, value } as FieldADTValue
                return fieldConvertValue(fieldADTValue)
            }, [])
            const handleRecordClick = useCallback(() => {
                if (!onRecordClick || !recordClickEvent) {
                    return
                }
                return handleActionTriggerWithLoading({
                    id: uniqueId,
                    actorEvent: recordClickEvent,
                    trigger: (id, event) => onRecordClick(id, event, record)
                })
            }, [handleActionTriggerWithLoading, onRecordClick, record, recordClickEvent, uniqueId])

            // 渲染带有控制是否不换行并超出显示省略号的下拉选项
            const renderGroupSelectField = useCallback(
                (config: GroupFieldValue, record: RecordLikeProtocol) => {
                    const { content, id: recordId } = record
                    const { id, wrap } = config
                    const field = schema[id]
                    const data = content?.[id]?.value
                    const fieldValue = { ...field, value: data } as FieldADTValue
                    const realValue = fieldConvertValue(fieldValue)
                    const isRender = !!field && !!realValue
                    return (
                        isRender && (
                            <ViewFieldValuePreviewer data={data} isWrap={wrap} field={field} disabled width={width} previewHost="gallery" />
                        )
                    )
                },
                [schema, width]
            )

            const galleryExtraStyles = useMemo<React.CSSProperties>(() => {
                return { cursor: loadings[id] ? 'not-allowed' : 'pointer', ...style }
            }, [id, loadings, style])

            if (!record || !recordContent) {
                return null
            }

            if (renderItem) {
                return <>{renderItem(record) || ''}</>
            }

            const isBorderStyle = recordStyle === GALLERY_CARD_STYLE.border

            const filterEmptyHeadTags = produce(headTags, draft => {
                if (draft?.fields) {
                    draft.fields = draft.fields.filter(config => {
                        const { content } = record
                        const { id } = config
                        const field = schema[id]
                        const data = content?.[id]?.value
                        const fieldValue = { ...field, value: data } as FieldADTValue
                        return !verifyFieldValueEmpty(fieldValue)
                    })
                }
            })
            const filterEmptyContentTags = produce(contentTags, draft => {
                if (draft?.fields) {
                    draft.fields = draft.fields.filter(config => {
                        const { content } = record
                        const { id } = config
                        const field = schema[id]
                        const data = content?.[id]?.value
                        const fieldValue = { ...field, value: data } as FieldADTValue
                        return !verifyFieldValueEmpty(fieldValue)
                    })
                }
            })
            const filterEmptyFootTags = produce(footTags, draft => {
                if (draft?.fields) {
                    draft.fields = draft.fields.filter(config => {
                        const { content } = record
                        const { id } = config
                        const field = schema[id]
                        const data = content?.[id]?.value
                        const fieldValue = { ...field, value: data } as FieldADTValue
                        return !verifyFieldValueEmpty(fieldValue)
                    })
                }
            })

            const showHeadTitle = !!headTitle?.show && !!headTitle?.field
            const showHeadTag = !!filterEmptyHeadTags?.show && !!filterEmptyHeadTags.fields && filterEmptyHeadTags.fields.length > 0

            const hasContent =
                isAvatarVisible(headAvatar) ||
                isBasicFieldVisible(headTitle) ||
                isHeadVisible(filterEmptyHeadTags) ||
                isContentVisible(filterEmptyContentTags) ||
                isFooterVisible(filterEmptyFootTags)

            return (
                <SC.GalleryItemCard
                    key={id}
                    ref={mergeRefs([ref, innerRef])}
                    data-key={`${id}&${record.id}`}
                    data-record-id={record.id}
                    onClick={handleRecordClick}
                    recordStyle={recordStyle}
                    enableHoverStyle={clickable}
                    radius={16}
                    withBorder={isBorderStyle}
                    style={galleryExtraStyles}
                >
                    <RowHighlighter viewFields={columns} recordContent={recordContent} recordConditions={rowStyleConditions} zIndex={-1} />
                    {/* 2023-09-06 14:06:36 灿白说注释，后面只有表格和高级表格有 CheckBox */}
                    {/* 封面 */}
                    {!!headCover?.show && (
                        <Card.Section style={{ margin: 0, padding: 0, paddingBottom: hasContent ? 8 : 16 }}>
                            <div
                                style={{
                                    width: '100%',
                                    borderRadius: 12,
                                    aspectRatio: `${coverRatio.toString()}`,
                                    background:
                                        headCover.field && getImageFromField(recordContent[headCover.field])
                                            ? undefined
                                            : 'var(--color-white)'
                                }}
                            >
                                {headCover.field && getImageFromField(recordContent[headCover.field]) && (
                                    <img
                                        src={scaleOssImg(
                                            getImageFullUrlInApplication(appId, getImageFromField(recordContent[headCover.field]))
                                        )}
                                        alt=""
                                        style={{
                                            width: '100%',
                                            borderRadius: 12,
                                            objectFit: headCover.objectFit,
                                            aspectRatio: `${coverRatio.toString()}`
                                        }}
                                    />
                                )}
                            </div>
                        </Card.Section>
                    )}

                    {hasContent && (
                        <SC.GalleryBody sx={{ flex: 1, padding: 8 }} avatarPosition={headAvatar?.position}>
                            {/* 头像 */}
                            {!!headAvatar?.show && (
                                <Avatar
                                    size={AVATAR_SIZES.get(headAvatar.size ?? GALLERY_AVATAR_SIZE.md)}
                                    src={
                                        headAvatar.field &&
                                        scaleOssImg(getImageFullUrlInApplication(appId, getImageFromField(recordContent[headAvatar.field])))
                                    }
                                    radius={
                                        headAvatar.shape === GALLERY_AVATAR_SHAPE.square
                                            ? AVATAR_RADIUS.get(headAvatar.size ?? GALLERY_AVATAR_SIZE.md)
                                            : AVATAR_SIZES.get(headAvatar.size ?? GALLERY_AVATAR_SIZE.md)
                                    }
                                    style={{ alignSelf: avatarAlignSelf }}
                                >
                                    <Image />
                                </Avatar>
                            )}
                            <SC.GalleryBody sx={{ flex: 1 }}>
                                {/* 头部部分 */}
                                {(showHeadTitle || showHeadTag) && (
                                    <SC.GalleryBody>
                                        {/* 头部标题及标签 */}
                                        <SC.GalleryHeader>
                                            {isBasicFieldVisible(headTitle) && (
                                                <div style={{ fontSize: 16, fontWeight: 600, textAlign: headTitle.align }}>
                                                    {getFieldValue(schema[headTitle.field], recordContent[headTitle.field]?.value)}
                                                </div>
                                            )}

                                            {isHeadVisible(filterEmptyHeadTags) ? (
                                                <Group noWrap spacing={6} position={filterEmptyHeadTags.align}>
                                                    {(() => {
                                                        const { fields, showLabel, align } = filterEmptyHeadTags
                                                        return fields.map((field, index, array) => (
                                                            <SC.GalleryHeadTag
                                                                key={field.id}
                                                                style={{
                                                                    textAlign: align,
                                                                    maxWidth: `${100 / fields.length}%`
                                                                }}
                                                            >
                                                                {showLabel ? (
                                                                    <SC.Label>
                                                                        {columns.find(item => item.fieldId === field.id)?.title}
                                                                    </SC.Label>
                                                                ) : null}
                                                                {renderGroupSelectField(field, record)}
                                                                {index !== array.length - 1 && (
                                                                    <SC.WithTextDivider direction="vertical" solidWidth={2} />
                                                                )}
                                                            </SC.GalleryHeadTag>
                                                        ))
                                                    })()}
                                                </Group>
                                            ) : null}
                                        </SC.GalleryHeader>
                                    </SC.GalleryBody>
                                )}

                                {/* 内容部分 */}
                                {isContentVisible(filterEmptyContentTags) && (
                                    <Stack spacing={16} sx={{ flex: 1 }}>
                                        {filterEmptyContentTags.fields.map(field => (
                                            <Box
                                                key={field.id}
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: breakPoint === BreakPointSize.xs ? 'column' : 'row',
                                                    justifyContent: filterEmptyContentTags.align,
                                                    overflow: 'hidden',
                                                    gap: 8,
                                                    alignItems:
                                                        breakPoint === BreakPointSize.xs
                                                            ? filterEmptyContentTags.align === 'right'
                                                                ? 'flex-end'
                                                                : filterEmptyContentTags.align
                                                            : 'baseline'
                                                }}
                                            >
                                                {filterEmptyContentTags.showLabel ? (
                                                    <SC.Label
                                                        style={{
                                                            flex: breakPoint === BreakPointSize.xs ? undefined : '0 0 30%'
                                                        }}
                                                    >
                                                        {columns.find(item => item.fieldId === field.id)?.title}
                                                    </SC.Label>
                                                ) : null}
                                                <div>{renderGroupSelectField(field, record)}</div>
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                                {/* 底部 */}
                                {isFooterVisible(filterEmptyFootTags) && (
                                    <Group
                                        noWrap
                                        spacing={6}
                                        position={filterEmptyFootTags.align}
                                        sx={{ marginTop: 'auto', paddingTop: 24 }}
                                    >
                                        {filterEmptyFootTags.fields.map((field, index, array) => (
                                            <Fragment key={field.id}>
                                                {filterEmptyFootTags.showLabel ? (
                                                    <SC.Label style={{ fontSize: 12 }}>
                                                        {columns.find(item => item.fieldId === field.id)?.title}:
                                                    </SC.Label>
                                                ) : null}
                                                <SC.CanEllipsisText size="xs" color="var(--color-gray-500)" ellipsis={!field.wrap}>
                                                    {getFieldValue(schema[field.id], recordContent[field.id]?.value)}
                                                </SC.CanEllipsisText>
                                                {index !== array.length - 1 && <SC.WithTextDivider direction="vertical" solidWidth={2} />}
                                            </Fragment>
                                        ))}
                                    </Group>
                                )}
                            </SC.GalleryBody>
                        </SC.GalleryBody>
                    )}
                    {recordOperatorFooterActions && (
                        <SC.GalleryActionWrapper
                            ref={footerActionsContainerRef}
                            style={
                                cardBreakPoint === 'sm'
                                    ? {
                                          flexDirection: 'column'
                                      }
                                    : {
                                          flexDirection: 'row'
                                      }
                            }
                        >
                            {recordOperatorFooterActions.map(item => {
                                const recordUniqueId = getWithRecordId(uniqueId, id)
                                const eventId = getWithItemId(recordUniqueId, item.id)
                                return (
                                    <ActionButton
                                        key={item.id}
                                        radius="8"
                                        // loading={loadings[item.id]}
                                        disabled={loadings[item.id]}
                                        config={{ ...item, fillWay: cardBreakPoint === 'sm' ? 'auto' : item.fillWay }}
                                        onClick={ev => {
                                            ev.stopPropagation()
                                            const actorEvent = item.eventList?.find(ev => ev.type === EVENT_TYPE.CLICK)
                                            if (!actorEvent) {
                                                return
                                            }
                                            onRecordOperatorActionTrigger &&
                                                handleActionTriggerWithLoading({
                                                    id: eventId,
                                                    actorEvent,
                                                    trigger: (id, actorEvent) => onRecordOperatorActionTrigger(id, actorEvent, record)
                                                })
                                        }}
                                        onRenderTitle={v => onRenderButtonTitle(v, record)}
                                    />
                                )
                            })}
                        </SC.GalleryActionWrapper>
                    )}
                </SC.GalleryItemCard>
            )

            // 补偿非卡片风格时的6px outline
            // return isBorderStyle ? dom : <div style={{ padding: 6, height: '100%' }}>{dom}</div>
        }
    )
)
